import { Channel, Message, Thread, User, Meeting, AgentSummary } from './types';

export const mockUsers: User[] = [
  {
    id: 'user-001',
    name: 'Sarah Chen',
    avatar: '/avatars/sarah.jpg',
    status: 'online',
    role: 'Project Manager'
  },
  {
    id: 'user-002',
    name: 'Alex Rodriguez',
    avatar: '/avatars/alex.jpg',
    status: 'busy',
    role: 'Developer'
  },
  {
    id: 'user-003',
    name: 'Jamie Taylor',
    avatar: '/avatars/jamie.jpg',
    status: 'away',
    role: 'Designer'
  },
  {
    id: 'user-004',
    name: 'Morgan Lee',
    avatar: '/avatars/morgan.jpg',
    status: 'offline',
    role: 'Product Owner'
  },
  {
    id: 'agent-001',
    name: 'ProjectBot',
    avatar: '/avatars/projectbot.jpg',
    status: 'online',
    isAgent: true,
    role: 'Assistant'
  },
  {
    id: 'agent-002',
    name: 'MeetingScribe',
    avatar: '/avatars/meetingscribe.jpg',
    status: 'online',
    isAgent: true,
    role: 'Meeting Assistant'
  }
];

export const mockChannels: Channel[] = [
  {
    id: 'channel-001',
    name: 'Digital Transformation',
    type: 'project',
    description: 'Main channel for the Digital Transformation project',
    members: ['user-001', 'user-002', 'user-003', 'user-004', 'agent-001'],
    unreadCount: 3,
    isPrivate: false,
    createdAt: new Date('2024-07-01'),
    createdBy: 'user-001',
    pinnedMessages: ['msg-003']
  },
  {
    id: 'channel-002',
    name: 'Customer Portal Redesign',
    type: 'project',
    description: 'Discussions about the customer portal redesign',
    members: ['user-001', 'user-003', 'user-004', 'agent-001'],
    unreadCount: 0,
    isPrivate: false,
    createdAt: new Date('2024-07-05'),
    createdBy: 'user-001',
    pinnedMessages: []
  },
  {
    id: 'channel-003',
    name: 'API Integration',
    type: 'team',
    description: 'Technical discussions about API integrations',
    members: ['user-001', 'user-002', 'agent-001'],
    unreadCount: 5,
    isPrivate: false,
    createdAt: new Date('2024-07-10'),
    createdBy: 'user-002',
    pinnedMessages: []
  },
  {
    id: 'dm-001',
    name: 'Sarah Chen, Alex Rodriguez',
    type: 'direct',
    members: ['user-001', 'user-002'],
    unreadCount: 1,
    isPrivate: true,
    createdAt: new Date('2024-07-12'),
    createdBy: 'user-001',
    pinnedMessages: []
  },
  {
    id: 'dm-002',
    name: 'Sarah Chen, ProjectBot',
    type: 'direct',
    members: ['user-001', 'agent-001'],
    unreadCount: 0,
    isPrivate: true,
    createdAt: new Date('2024-07-15'),
    createdBy: 'user-001',
    pinnedMessages: []
  }
];

export const mockMessages: Record<string, Message[]> = {
  'channel-001': [
    {
      id: 'msg-001',
      channelId: 'channel-001',
      content: 'Good morning team! Let\'s discuss the progress on the Digital Transformation project.',
      sender: mockUsers[0], // Sarah
      timestamp: new Date('2024-07-20T09:00:00'),
      type: 'text'
    },
    {
      id: 'msg-002',
      channelId: 'channel-001',
      content: 'I\'ve completed the database migration plan. Here\'s the document for review.',
      sender: mockUsers[1], // Alex
      timestamp: new Date('2024-07-20T09:05:00'),
      type: 'text',
      attachments: [
        {
          id: 'attach-001',
          name: 'Database_Migration_Plan.pdf',
          type: 'application/pdf',
          url: '/files/Database_Migration_Plan.pdf',
          size: 2500000,
          thumbnailUrl: '/thumbnails/pdf.png'
        }
      ]
    },
    {
      id: 'msg-003',
      channelId: 'channel-001',
      content: 'I\'ve analyzed the project timeline and identified potential bottlenecks. The database migration is on the critical path and requires additional resources to meet the deadline.',
      sender: mockUsers[4], // ProjectBot
      timestamp: new Date('2024-07-20T09:10:00'),
      type: 'agent',
      isPinned: true,
      metadata: {
        analysis: {
          criticalPath: ['Database Migration', 'API Integration', 'User Acceptance Testing'],
          riskAreas: ['Resource Allocation', 'Timeline Constraints'],
          recommendations: ['Allocate 2 additional developers to database migration', 'Start UAT preparation in parallel']
        }
      }
    },
    {
      id: 'msg-004',
      channelId: 'channel-001',
      content: 'Thanks for the analysis, ProjectBot. @Alex can you work with the team to address these resource needs?',
      sender: mockUsers[0], // Sarah
      timestamp: new Date('2024-07-20T09:15:00'),
      type: 'text',
      mentions: ['user-002']
    },
    {
      id: 'msg-005',
      channelId: 'channel-001',
      content: 'Sure, I\'ll coordinate with the team and update the resource allocation plan.',
      sender: mockUsers[1], // Alex
      timestamp: new Date('2024-07-20T09:20:00'),
      type: 'text',
      threadId: 'msg-004',
      threadRepliesCount: 1
    }
  ],
  'channel-002': [
    {
      id: 'msg-006',
      channelId: 'channel-002',
      content: 'The design mockups for the customer portal are ready for review.',
      sender: mockUsers[2], // Jamie
      timestamp: new Date('2024-07-19T14:00:00'),
      type: 'text',
      attachments: [
        {
          id: 'attach-002',
          name: 'Portal_Mockups.fig',
          type: 'application/figma',
          url: '/files/Portal_Mockups.fig',
          size: 15000000,
          thumbnailUrl: '/thumbnails/figma.png'
        }
      ]
    },
    {
      id: 'msg-007',
      channelId: 'channel-002',
      content: 'These look great! I especially like the new dashboard layout.',
      sender: mockUsers[3], // Morgan
      timestamp: new Date('2024-07-19T14:30:00'),
      type: 'text',
      reactions: {
        '👍': {
          emoji: '👍',
          count: 2,
          users: ['user-001', 'user-003']
        },
        '❤️': {
          emoji: '❤️',
          count: 1,
          users: ['user-004']
        }
      }
    }
  ],
  'dm-001': [
    {
      id: 'msg-008',
      channelId: 'dm-001',
      content: 'Hey Alex, do you have a moment to discuss the API integration challenges?',
      sender: mockUsers[0], // Sarah
      timestamp: new Date('2024-07-20T10:00:00'),
      type: 'text'
    },
    {
      id: 'msg-009',
      channelId: 'dm-001',
      content: 'Sure, I\'m available now. What specific challenges are you concerned about?',
      sender: mockUsers[1], // Alex
      timestamp: new Date('2024-07-20T10:05:00'),
      type: 'text'
    }
  ],
  'dm-002': [
    {
      id: 'msg-010',
      channelId: 'dm-002',
      content: 'ProjectBot, can you generate a status report for the Digital Transformation project?',
      sender: mockUsers[0], // Sarah
      timestamp: new Date('2024-07-20T11:00:00'),
      type: 'text'
    },
    {
      id: 'msg-011',
      channelId: 'dm-002',
      content: 'Here\'s the status report for the Digital Transformation project as of today:',
      sender: mockUsers[4], // ProjectBot
      timestamp: new Date('2024-07-20T11:01:00'),
      type: 'agent',
      metadata: {
        statusReport: {
          projectName: 'Digital Transformation',
          status: 'On Track',
          completionPercentage: 45,
          milestonesCompleted: 3,
          milestonesRemaining: 5,
          nextMilestone: 'Database Migration Completion',
          nextMilestoneDate: '2024-08-05',
          budgetStatus: 'Within Budget',
          teamPerformance: 'High',
          risks: [
            { description: 'Resource constraints for database migration', severity: 'Medium', mitigation: 'Allocating additional resources' },
            { description: 'Integration with legacy systems', severity: 'High', mitigation: 'Creating detailed integration plan' }
          ]
        }
      }
    }
  ]
};

export const mockThreads: Thread[] = [
  {
    id: 'msg-004', // Parent message ID
    channelId: 'channel-001',
    parentMessage: mockMessages['channel-001'][3], // msg-004
    replies: [
      mockMessages['channel-001'][4], // msg-005
      {
        id: 'msg-012',
        channelId: 'channel-001',
        content: 'I\'ve updated the resource allocation plan. We\'ll need two more developers for the database migration.',
        sender: mockUsers[1], // Alex
        timestamp: new Date('2024-07-20T10:30:00'),
        type: 'text',
        threadId: 'msg-004'
      }
    ],
    participants: ['user-001', 'user-002'],
    lastReplyAt: new Date('2024-07-20T10:30:00'),
    isFollowing: true
  }
];

export const mockMeetings: Meeting[] = [
  {
    id: 'meeting-001',
    title: 'Digital Transformation Weekly Sync',
    description: 'Weekly team sync to discuss progress and blockers',
    startTime: new Date('2024-07-22T10:00:00'),
    endTime: new Date('2024-07-22T11:00:00'),
    organizer: 'user-001', // Sarah
    participants: ['user-001', 'user-002', 'user-003', 'user-004', 'agent-002'],
    status: 'scheduled',
    channelId: 'channel-001',
    agendaItems: [
      'Project status update',
      'Database migration progress',
      'Resource allocation discussion',
      'Next steps and action items'
    ]
  },
  {
    id: 'meeting-002',
    title: 'Customer Portal Design Review',
    description: 'Review and feedback session for the new portal designs',
    startTime: new Date('2024-07-21T14:00:00'),
    endTime: new Date('2024-07-21T15:30:00'),
    organizer: 'user-003', // Jamie
    participants: ['user-001', 'user-003', 'user-004', 'agent-002'],
    status: 'completed',
    channelId: 'channel-002',
    recordingUrl: '/recordings/meeting-002.mp4',
    transcriptId: 'transcript-002',
    notes: 'The team reviewed the new portal designs and provided feedback. Overall, the designs were well-received with some minor suggestions for improvement.',
    actionItems: [
      {
        id: 'action-001',
        description: 'Update the dashboard layout based on feedback',
        assignee: 'user-003', // Jamie
        dueDate: new Date('2024-07-25'),
        status: 'in-progress'
      },
      {
        id: 'action-002',
        description: 'Prepare user testing plan for the new designs',
        assignee: 'user-004', // Morgan
        dueDate: new Date('2024-07-28'),
        status: 'pending'
      }
    ]
  }
];

export const mockAgentSummaries: AgentSummary[] = [
  {
    id: 'summary-001',
    channelId: 'channel-001',
    messageIds: ['msg-001', 'msg-002', 'msg-003', 'msg-004', 'msg-005'],
    content: 'The team discussed the Digital Transformation project progress. Alex completed the database migration plan. ProjectBot identified bottlenecks in the timeline, particularly with the database migration requiring additional resources. Sarah asked Alex to address the resource needs, and Alex agreed to update the resource allocation plan.',
    createdAt: new Date('2024-07-20T12:00:00'),
    type: 'channel'
  },
  {
    id: 'summary-002',
    channelId: 'channel-002',
    messageIds: ['msg-006', 'msg-007'],
    content: 'Jamie shared design mockups for the customer portal. Morgan provided positive feedback, particularly about the new dashboard layout. The mockups were well-received by the team.',
    createdAt: new Date('2024-07-19T16:00:00'),
    type: 'channel'
  },
  {
    id: 'summary-003',
    channelId: 'channel-002',
    messageIds: [], // Meeting transcript messages
    content: 'In the Customer Portal Design Review meeting, the team reviewed the new portal designs. Key decisions: (1) Proceed with the dashboard layout with minor adjustments, (2) Begin user testing preparation, (3) Schedule a follow-up review for the updated designs next week. Action items were assigned to Jamie and Morgan.',
    createdAt: new Date('2024-07-21T15:35:00'),
    type: 'meeting',
    metadata: {
      meetingId: 'meeting-002',
      decisions: [
        'Proceed with the dashboard layout with minor adjustments',
        'Begin user testing preparation',
        'Schedule a follow-up review for the updated designs next week'
      ],
      actionItems: [
        {
          id: 'action-001',
          description: 'Update the dashboard layout based on feedback',
          assignee: 'user-003', // Jamie
          dueDate: '2024-07-25'
        },
        {
          id: 'action-002',
          description: 'Prepare user testing plan for the new designs',
          assignee: 'user-004', // Morgan
          dueDate: '2024-07-28'
        }
      ]
    }
  }
];

export const getMessagesForChannel = (channelId: string): Message[] => {
  return mockMessages[channelId] || [];
};

export const getThreadById = (threadId: string): Thread | undefined => {
  return mockThreads.find(thread => thread.id === threadId);
};

export const getUserById = (userId: string): User | undefined => {
  return mockUsers.find(user => user.id === userId);
};

export const getChannelById = (channelId: string): Channel | undefined => {
  return mockChannels.find(channel => channel.id === channelId);
};
